import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import Axios from "axios";
import Localizer from "@Scripts/Common/Localizer";
import FormHelper from "@Scripts/Common/FormHelper";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";
import DataHelper, { DataHelperConfiguration } from "@Scripts/Common/DataHelper";
import { easepick } from '@easepick/core';
import { RangePlugin } from '@easepick/range-plugin';
import { LockPlugin } from '@easepick/lock-plugin';

@Component({ name: "DealerPointDetail" })
export default class DealerPointDetail extends Vue {
    public localizer: any = Localizer;
    public dataHelperConfiguration: DataHelperConfiguration = { culture: (<any>window).App.userCulture };
    public isLoading: boolean = true;

    public dealerPoint: any = null;
    public bikeModels: any[] = [];
    public accessories: any[] = [];
    public accessoryTypes: any[] = [];
    public selectedModel: any = null;
    public showModelSuggestions: boolean = false;
    public youRentRequest: any = {};
    public selectedAccessories: any[] = [];

    public rentalDayNumber: number = null;
    public rentalPrice: number = null;
    public bikePrice: number = null;
    public accessoriesPrice: number = null;
    public showPriceDetail: boolean = false;

    public isSuccess: boolean = false;
    public termsOfService: string = null;
    public isTermsOfServiceShown: boolean = false;
    public validationErrors: any[] = [];

    @Prop() readonly language: string;
    @Prop() readonly dealerPointCode: string;
    @Prop() readonly dateStart: string;
    @Prop() readonly dateEnd: string;
    @Prop() readonly modelName: string;
    @Prop() readonly recaptchaSiteKey: string;

    public async created() {
        await this.getDealerPointData();

        if (this.modelName) {
            this.selectedModel = this.bikeModels.find(x => x.name === this.modelName);
        }

        this.getAccessories();

        this.isLoading = false;

        this.$nextTick(function () {
            this.loadDatePicker();
        });
    }

    public async getDealerPointData() {
        let data = (await Axios.post((<any>window).App.routes.YOURent_GetDealerPointData, { dealerPointCode: this.dealerPointCode })).data;
        this.dealerPoint = data.dealerPoint;
        this.bikeModels = data.bikeModels;
    }

    public async getAccessories() {
        let data = (await Axios.post((<any>window).App.routes.YOURent_GetAccessories, { dealerCode: this.dealerPoint.dealerCode, dealerPointCode: this.dealerPoint.code, modelName: this.selectedModel ? this.selectedModel.name : null })).data;
        this.accessories = data.accessories;
        this.accessoryTypes = data.accessoryTypes;
    }

    public async getRentalPrice() {
        FormHelper.removeFocus();
        this.validationErrors = [];

        if (this.youRentRequest.dateStart && this.youRentRequest.dateEnd && this.youRentRequest.timePickUp && this.youRentRequest.timeReturn && this.selectedModel && this.dealerPoint) {
            try {
                let data = (await Axios.post((<any>window).App.routes.YOURent_GetRentalPrice, {
                    dateStart: this.youRentRequest.dateStart,
                    dateEnd: this.youRentRequest.dateEnd,
                    timePickUp: this.youRentRequest.timePickUp,
                    timeReturn: this.youRentRequest.timeReturn,
                    modelName: this.selectedModel.name,
                    dealerPointCode: this.dealerPoint.code,
                    accessories: this.selectedAccessories
                })).data;

                this.bikePrice = data.bikePrice;
                this.accessoriesPrice = data.accessoriesPrice;
                this.rentalPrice = data.totalPrice;
                this.rentalDayNumber = data.dayNumber;

            } catch (error) {
                if (error.response.status === 400) {
                    this.validationErrors = FormHelper.getValidationErrors(error.response.data);
                }
            }
        }
    }

    public async loadDatePicker() {
        let lockedDays = (await Axios.post((<any>window).App.routes.YOURent_GetLockedDays, { dealerCode: this.dealerPoint.dealerCode, dealerPointCode: this.dealerPoint.code, modelName: this.selectedModel ? this.selectedModel.name : null })).data;

        const picker = new easepick.create({
            element: document.getElementById("DealerDatepicker"),
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/core@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/lock-plugin@1.2.1/dist/index.css',
                '/styles/easepick.css',
            ],
            format: "DD\/MM\/YYYY",
            plugins: [LockPlugin, RangePlugin],
            LockPlugin: {
                minDate: Date.now(),
                inseparable: true,
                filter(date: any) {
                    let formatdate = date.format('YYYY-MM-DD');
                    return lockedDays.includes(formatdate);
                }
            },
            RangePlugin: {
                tooltip: false
            }
        });

        if (this.dateStart && this.dateEnd) {
            picker.setDateRange(this.dateStart, this.dateEnd);

            this.youRentRequest.dateStart = this.dateStart;
            this.youRentRequest.dateEnd = this.dateEnd;
        }

        picker.on("select", async () => {
            this.youRentRequest.dateStart = this.getDateWithOutOffset(picker.getStartDate());
            this.youRentRequest.dateEnd = this.getDateWithOutOffset(picker.getEndDate());

            await this.getRentalPrice();
        });
    }

    public async selectModel(model: any) {
        if (this.selectedModel === model) {
            this.selectedModel = null;
        } else {
            this.selectedModel = model;
            this.getAccessories();
            this.getRentalPrice();
        }

        this.loadDatePicker();
    }

    public async clearModel() {
        this.selectedModel = null
    }

    public async togglePriceDetail() {
        this.showPriceDetail = !this.showPriceDetail;
    }

    public async showTermsOfService() {
        if (!this.termsOfService) {
            this.termsOfService = (await Axios.post((<any>window).App.routes.YOURent_GetTermsOfService)).data.text;
        }
        this.isTermsOfServiceShown = !this.isTermsOfServiceShown;
    }

    public showContract() {
        this.validationErrors = [];

        if (!this.selectedModel) {
            this.validationErrors.push({ message: this.localizer.get("YOURent.Common.ModelRequired") });
            return;
        }

        if (!this.youRentRequest.dateStart) {
            this.validationErrors.push({ message: this.localizer.get("YOURent.Common.PeriodRequired") });
            return;
        }

        if (!this.youRentRequest.dateEnd) {
            this.validationErrors.push({ message: this.localizer.get("YOURent.Common.PeriodRequired") });
            return;
        }

        if (!this.rentalPrice) {
            this.validationErrors.push({ message: this.localizer.get("YOURent.Common.ContractPreviewUnavailable") });
            return;
        }

        let url = "/noleggio/" + this.language + "/contratto/" + this.dealerPoint.dealerCode + "/" + this.dealerPoint.code + "/" + this.selectedModel.type + "/" + this.selectedModel.name + "?dateStart=" + this.youRentRequest.dateStart + "&dateEnd=" + this.youRentRequest.dateEnd + "&price=" + this.rentalPrice;

        window.open(url, "_blank");

        this.validationErrors = [];
    }

    public async saveRequest(intent: string) {
        FormHelper.removeFocus();
        this.validationErrors = [];

        (<any>window).grecaptcha.enterprise.ready(async () => {
            const token = await (<any>window).grecaptcha.enterprise.execute(this.recaptchaSiteKey, { action: 'Submit' });

            try {
                let response = await Axios.post((<any>window).App.routes.YOURent_SaveRequest, {
                    intent: intent,
                    dealerPointCode: this.dealerPoint.code,
                    modelName: this.selectedModel.name,
                    name: this.youRentRequest.name,
                    surname: this.youRentRequest.surname,
                    email: this.youRentRequest.email,
                    phone: this.youRentRequest.phone,
                    dateStart: this.youRentRequest.dateStart,
                    dateEnd: this.youRentRequest.dateEnd,
                    timePickUp: this.youRentRequest.timePickUp,
                    timeReturn: this.youRentRequest.timeReturn,
                    accessories: this.selectedAccessories,
                    isPrivacyA: this.youRentRequest.isPrivacyA,
                    isTermsOfService: this.youRentRequest.isTermsOfService
                }, {
                    headers: { "RecaptchaVerificationToken": token }
                });

                if (intent === "Payment") {
                    GTagHelper.sendEvent("form_submit", "you_rent_payment");
                } else if (intent === "Booking") {
                    GTagHelper.sendEvent("form_submit", "you_rent_booking");
                }

                if (response.data.returnUrl) {
                    window.location.href = response.data.returnUrl;
                } else {
                    this.isSuccess = true;
                }
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.validationErrors = FormHelper.getValidationErrors(error.response.data);
                }
            }
        });
    }

    public getDateWithOutOffset(date: Date) {
        const offset = date.getTimezoneOffset()
        date = new Date(date.getTime() - (offset * 60 * 1000))
        return date.toISOString().split('T')[0]
    }

    public toggleModelSuggestions() {
        this.showModelSuggestions = !this.showModelSuggestions;
    }

    public setAnalytics(param: string) {
        switch (param) {
            case "phone":
                GTagHelper.sendEvent("click", "you_rent_phone", this.dealerPoint.company);
                break;
            case "website":
                GTagHelper.sendEvent("click", "you_rent_website", this.dealerPoint.dealerSiteSlug);
                break;
        }
    }

    public convertNumberToString(number: number): string {
        return DataHelper.convertNumberToString(number, this.dataHelperConfiguration);
    }

    public back() {
        this.$router.push({ name: "Locator", params: { language: this.language } });
    }
}